import { useEffect } from 'react';

/*
 * Every modal in the app, made to behave like one.
 *
 * About thirty dialogs are written as <div class="modal-overlay"><div
 * class="modal">, each closed by its own setter, and none of them took focus,
 * kept Tab inside, closed on Esc, or gave focus back to what opened it. Rather
 * than patch thirty call sites, this watches the document once:
 *
 *   - a dialog appearing is marked role="dialog" aria-modal="true", labelled by
 *     its own heading, and focus moves into it;
 *   - Tab and Shift+Tab cycle within the topmost dialog;
 *   - Esc presses the dialog's own close button, so closing runs the same code
 *     the button runs (a dialog with no close button is left alone);
 *   - when it closes, focus returns to the element that opened it.
 *
 * Side panels that are dialogs but not modal (the floating chat) are not
 * matched: only .modal and [aria-modal="true"].
 */

const DIALOG_SELECTOR = '.modal, [role="dialog"][aria-modal="true"]';
const CLOSE_SELECTOR = '.modal-close, [data-dialog-close], [aria-label="Close"], [aria-label^="Close "]';
const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

const visible = (el) => Boolean(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
const focusablesIn = (dialog) => Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(visible);
const openDialogs = () => Array.from(document.querySelectorAll(DIALOG_SELECTOR)).filter(visible);
const topDialog = () => {
    const dialogs = openDialogs();
    return dialogs[dialogs.length - 1] || null;
};

let headingSeq = 0;

function adopt(dialog) {
    if (dialog.dataset.a11yAdopted) return;
    dialog.dataset.a11yAdopted = 'true';
    // Remembered so closing can hand focus back where it came from.
    dialog.__openerEl = document.activeElement;

    if (!dialog.getAttribute('role')) dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (!dialog.getAttribute('aria-label') && !dialog.getAttribute('aria-labelledby')) {
        const heading = dialog.querySelector('h1, h2, h3, h4');
        if (heading) {
            headingSeq += 1;
            if (!heading.id) heading.id = `dialog-title-${headingSeq}`;
            dialog.setAttribute('aria-labelledby', heading.id);
        }
    }
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    // Focus the first field if there is one (that is what the dialog is for),
    // otherwise the dialog itself. Not the close button: Enter on arrival
    // should never dismiss what just opened.
    const target = focusablesIn(dialog).find(el => !el.matches(CLOSE_SELECTOR)) || dialog;
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
}

export function useModalAccessibility() {
    useEffect(() => {
        const adopted = new Set();

        const sync = () => {
            openDialogs().forEach((dialog) => {
                adopt(dialog);
                adopted.add(dialog);
            });
            // A dialog that left the page: give focus back to its opener.
            Array.from(adopted).forEach((dialog) => {
                if (document.contains(dialog) && visible(dialog)) return;
                adopted.delete(dialog);
                const opener = dialog.__openerEl;
                if (opener && document.contains(opener) && typeof opener.focus === 'function') {
                    opener.focus({ preventScroll: true });
                }
            });
        };

        const onKeyDown = (event) => {
            const dialog = topDialog();
            if (!dialog) return;

            if (event.key === 'Escape') {
                const close = dialog.querySelector(CLOSE_SELECTOR);
                if (close && !close.disabled) {
                    event.preventDefault();
                    close.click();
                }
                return;
            }

            if (event.key === 'Tab') {
                const items = focusablesIn(dialog);
                if (items.length === 0) {
                    event.preventDefault();
                    dialog.focus();
                    return;
                }
                const first = items[0];
                const last = items[items.length - 1];
                const active = document.activeElement;
                if (event.shiftKey && (active === first || !dialog.contains(active))) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        const observer = new MutationObserver(sync);
        observer.observe(document.body, { childList: true, subtree: true });
        document.addEventListener('keydown', onKeyDown, true);
        sync();
        return () => {
            observer.disconnect();
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, []);
}
